'use client';

import { getCookie } from '@/utils/cookies';
import type { ProviderConfig } from 'launchdarkly-react-client-sdk';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';

import { buildLaunchDarklyProviderConfig } from './launchdarkly-provider.utils';

interface LaunchDarklyClientProviderProps {
  children: React.ReactNode;
}

// API domains for distributed tracing (only production APIs)
// Note: localhost excluded because serverless-offline doesn't handle traceparent header in preflight
const tracingOrigins = ['api.masp.digital', 'api-dev.masp.digital'];

// URL patterns to exclude from network recording (sensitive endpoints)
const urlBlocklist = [
  '*/api/*/auth*',
  '*/api/*/login*',
  '*/api/*/password*',
  '*/api/*/token*',
  '*cognito*',
  '*payment*',
  '*pix*',
  '*credit*',
  '*card*',
];

// Check consent level from cookie (synchronous for provider initialization)
function getConsentLevel(): 'none' | 'analytics' | 'full' {
  if (typeof window === 'undefined') return 'none';

  try {
    const cookieValue = getCookie('lgpd_consent', true);
    if (cookieValue) {
      const parsed = JSON.parse(cookieValue);
      return parsed.level || 'none';
    }
  } catch {
    // Ignore parsing errors
  }
  return 'none';
}

// Only enable observability/metrics in production
const isProduction = process.env.NEXT_PUBLIC_APP_ENV === 'production';

export default function LaunchDarklyClientProvider({ children }: LaunchDarklyClientProviderProps) {
  const [isClient, setIsClient] = useState(false);
  const [plugins, setPlugins] = useState<NonNullable<NonNullable<ProviderConfig['options']>['plugins']>>([]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient || !isProduction) return;

    const loadPlugins = async () => {
      const consentLevel = getConsentLevel();
      const loaded: any[] = [];

      const { default: Observability } = await import('@launchdarkly/observability');
      loaded.push(
        new Observability({
          tracingOrigins,
          networkRecording: {
            enabled: consentLevel === 'analytics' || consentLevel === 'full',
            recordHeadersAndBody: false, // LGPD: never record request/response bodies
            urlBlocklist,
          },
        })
      );

      if (consentLevel === 'full') {
        const { default: SessionReplay } = await import('@launchdarkly/session-replay');
        loaded.push(
          new SessionReplay({
            privacySetting: 'strict', // LGPD: mask all text by default
          })
        );
      }

      setPlugins(loaded);
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => loadPlugins());
    } else {
      setTimeout(() => loadPlugins(), 2000);
    }
  }, [isClient]);

  // During SSR and initial hydration, render without LaunchDarkly
  // LDProvider is a context provider that doesn't add DOM elements,
  // so the DOM structure is the same in both cases
  if (!isClient) {
    return <>{children}</>;
  }

  const providerConfig = buildLaunchDarklyProviderConfig({
    clientSideID: process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID!,
    plugins,
  });

  return <LDProvider {...providerConfig}>{children}</LDProvider>;
}
